import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { useSetAtom } from 'jotai';
import { callSettingsAtom, DEFAULT_CALL_THRESHOLDS } from '@/lib/atoms';
import { useWorkspaceSettings } from '@/hooks/useWorkspaceSettings';
import { PhoneCallLogger } from "./PhoneCallLogger";

interface PhoneCallState {
  isOpen: boolean;
  isMinimized: boolean;
  contactId: string;
  contactName: string;
  phoneNumber: string;
  startTime: number | null;
  isActive: boolean;
}

interface PhoneCallContextType {
  startCall: (contactId: string, phoneNumber: string, contactName: string) => void;
  endCall: () => void;
  minimizeCall: () => void;
  maximizeCall: () => void;
  callState: PhoneCallState;
  elapsedSeconds: number;
}

const PhoneCallContext = createContext<PhoneCallContextType | undefined>(undefined);

const initialState: PhoneCallState = {
  isOpen: false,
  isMinimized: false,
  contactId: '',
  contactName: '',
  phoneNumber: '',
  startTime: null,
  isActive: false,
};

export function PhoneCallProvider({ children }: { children: React.ReactNode }) {
  const [callState, setCallState] = useState<PhoneCallState>(initialState);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (callState.isActive && callState.startTime) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - callState.startTime!) / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [callState.isActive, callState.startTime]);

  const startCall = useCallback((contactId: string, phoneNumber: string, contactName: string) => {
    setCallState({
      isOpen: true, isMinimized: false,
      contactId, contactName, phoneNumber,
      startTime: Date.now(), isActive: true,
    });
    setElapsedSeconds(0);
  }, []);

  const endCall = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setCallState(initialState);
    setElapsedSeconds(0);
  }, []);

  const minimizeCall = useCallback(() => {
    setCallState((prev) => ({ ...prev, isMinimized: true }));
  }, []);

  const maximizeCall = useCallback(() => {
    setCallState((prev) => ({ ...prev, isMinimized: false }));
  }, []);

  // Sync call settings from workspace settings
  const { settings } = useWorkspaceSettings();
  const setCallSettings = useSetAtom(callSettingsAtom);

  useEffect(() => {
    if (settings?.call_thresholds && settings.call_thresholds.length > 0) {
      setCallSettings(settings.call_thresholds);
    } else {
      setCallSettings(DEFAULT_CALL_THRESHOLDS);
    }
  }, [settings?.call_thresholds, setCallSettings]);

  return (
    <PhoneCallContext.Provider value={{ startCall, endCall, minimizeCall, maximizeCall, callState, elapsedSeconds }}>
      {children}
      {callState.isOpen && (
        <PhoneCallLogger
          onClose={endCall}
          onMinimize={callState.isMinimized ? maximizeCall : minimizeCall}
          isMinimized={callState.isMinimized}
        />
      )}
    </PhoneCallContext.Provider>
  );
}

export function usePhoneCall() {
  const context = useContext(PhoneCallContext);
  if (context === undefined) {
    throw new Error("usePhoneCall must be used within a PhoneCallProvider");
  }
  return context;
}
